import groupBy from 'object.groupby';
import { isPlatformNodeJS, debounce } from '@utils/common';
import { HOURS_TO_MS, configRefs } from '@utils/constants';
import { authenticateUserDataRequest } from './util.authenticate-userdata-request.js';

const fetchEntriesForComponentQueryKey = (userId, componentId) => ["component-framework/fetch-entries-for-component", userId, componentId];

/**
 * Query that extracts all entries for a given component and user.
 *
 * Implementation detail: The UserData API supports batching of component
 * entries for several components, and support for said batching can be found
 * in this file.
 */
const fetchEntriesForComponentQuery = (userId, componentId, meta) => {
  return {
    queryKey: fetchEntriesForComponentQueryKey(userId, componentId),
    queryFn: async () => {
      return await fetchEntriesForComponent(userId, componentId, meta.schema, meta.processors);
    },
    // fetching will always be handled explicitly using client.prefetchQuery()
    enabled: false,
    staleTime: isPlatformNodeJS() ? 0 : 1.5 * HOURS_TO_MS,
    gcTime: isPlatformNodeJS() ? 0 : 1.5 * HOURS_TO_MS,
    meta: {
      persist: false
    }
  };
};
let pendingResponses = {};

/**
 * Implementation that fetches all component entries for a specific component
 * for a specific user, using a batching abstraction.
 */
async function fetchEntriesForComponent(userId, componentId, schema, processors) {
  try {
    const res = await new Promise(resolve => {
      pendingResponses[componentId] ??= new Set();
      pendingResponses[componentId].add(resolve);
      fetchPendingEntries(userId);
    });
    if (!res.ok) {
      throw new Error(`[${res.status}]: ${res.error}`);
    }
    return await Promise.all(res.content.map(async entry => {
      const envelope = entry;
      const data = JSON.parse(envelope.content ?? "");
      delete envelope.content;
      try {
        const transformed = (await processors?.preValidation?.(data)) ?? data;
        const validatedData = schema.parse(transformed);
        return {
          state: "valid",
          envelope,
          data: (await processors?.postValidation?.(validatedData)) ?? validatedData
        };
      } catch (err) {
        console.log("fetchEntriesForComponent(): parsing data failed:", err);
        return {
          state: "invalid",
          data,
          envelope,
          error: err
        };
      }
    }));
  } catch (err) {
    console.log("fetchEntriesForComponent(): failed", {
      userId,
      componentId
    });
    throw err;
  }
}

/**
 * The UserData API allows for fetching entries for several components at once,
 * however DX prefers being able to request a single component per call. Thus
 * we use this method to batch several calls to limit requests to the backend.
 */
const fetchPendingEntries = debounce(async userId => {
  const pendingResponsesForCurrentCall = Object.fromEntries(Object.keys(pendingResponses).map(key => [key, new Set(pendingResponses[key])]));
  const componentIdList = Object.keys(pendingResponsesForCurrentCall);
  pendingResponses = {};
  const url = `${await configRefs.apis.userdata.get()}/api/ComponentEntries/ByUserAndComponents?userID=${encodeURIComponent(userId)}&componentIDs=${encodeURIComponent(componentIdList.join(","))}`;
  const res = await fetch(...authenticateUserDataRequest(url, {
    cache: "no-store"
  }));
  const errorText = await (async () => {
    if (res.status < 200 || res.status >= 300) {
      return await res.text();
    } else {
      return undefined;
    }
  })();
  const groupedData = await (async () => {
    if (!res.ok) {
      return;
    }
    const rawData = await res.json();
    return groupBy(rawData, elm => elm.componentID);
  })();
  for (const [componentID, pendingResponseSet] of Object.entries(pendingResponsesForCurrentCall)) {
    for (const pendingResponse of pendingResponseSet) {
      if (res.status === 404 || res.status === 204) {
        pendingResponse({
          ok: false,
          status: res.status,
          error: "not-found"
        });
        continue;
      }
      if (res.status < 200 || res.status >= 300) {
        pendingResponse({
          ok: false,
          status: res.status,
          error: errorText
        });
        continue;
      }
      pendingResponse({
        ok: true,
        content: groupedData?.[componentID] ?? []
      });
    }
  }
}, 50);

export { fetchEntriesForComponentQuery, fetchEntriesForComponentQueryKey };
//# sourceMappingURL=request.fetch-entries.js.map
